import React from "react";
import { Link } from "react-router-dom";
import { MapPin, Phone, Clock, Navigation } from "lucide-react";
import Seo, { localBusinessJsonLd, breadcrumbJsonLd } from "@/components/Seo";
import { Breadcrumbs, StarRating } from "@/components/common";
import { SITE, CITY_PAGES } from "@/mock";

export default function Locations() {
  const crumbs = [
    { name: "Home", path: "/" },
    { name: "Location", path: "/locations" },
  ];
  return (
    <>
      <Seo
        title="Store Location - Mattress Store in Fort Lauderdale, FL"
        description="Visit the MattressTown showroom at 2677 N Federal Hwy, Fort Lauderdale, FL. Open 7 days a week with same-day delivery across Miami-Dade, Broward and Palm Beach counties."
        path="/locations"
        jsonLd={[localBusinessJsonLd(), breadcrumbJsonLd(crumbs)]}
      />
      <Breadcrumbs items={crumbs} />

      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        <h1 className="text-3xl font-bold uppercase text-neutral-900 sm:text-4xl">Our Location</h1>
        <p className="mt-3 max-w-3xl text-neutral-600">
          MattressTown proudly serves Fort Lauderdale and all of Miami-Dade,
          Broward &amp; Palm Beach counties from our N Federal Highway showroom. Stop by to try our full lineup of
          premium mattresses - our family will help you find the perfect fit.
        </p>

        <div className="mt-8 grid gap-6 sm:gap-8 lg:grid-cols-2">
          <div className="overflow-hidden rounded-2xl border border-neutral-200 bg-white shadow-sm">
            <img src={SITE.storePhotos[0]} alt="MattressTown Fort Lauderdale storefront" className="h-64 w-full object-cover" />
            <div className="p-6">
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold text-neutral-900">Fort Lauderdale, FL</h2>
                <span className="flex items-center gap-1 text-sm font-semibold text-neutral-700"><StarRating value={5} size={14} /> {SITE.rating.toFixed(1)}</span>
              </div>
              <address className="mt-4 space-y-3 text-sm not-italic text-neutral-700">
                <p className="flex items-start gap-2"><MapPin className="mt-0.5 h-4 w-4 text-[#D0021B]" aria-hidden="true" /> {SITE.address.full}</p>
                <a href={SITE.phoneHref} className="flex items-center gap-2 hover:text-[#D0021B]"><Phone className="h-4 w-4 text-[#D0021B]" aria-hidden="true" /> {SITE.phone}</a>
                <div className="flex items-start gap-2"><Clock className="mt-0.5 h-4 w-4 text-[#D0021B]" aria-hidden="true" />
                  <ul>{SITE.hours.map((h) => (<li key={h.day}>{h.day}: {h.time}</li>))}</ul>
                </div>
              </address>
              <div className="mt-5 flex flex-wrap gap-3">
                <a href={SITE.address.mapsUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 rounded-full bg-[#D0021B] px-6 py-2.5 text-sm font-bold uppercase tracking-wide text-white hover:bg-[#a80016]"><Navigation className="h-4 w-4" aria-hidden="true" /> Get Directions</a>
                <Link to="/locations/fort-lauderdale" className="inline-flex items-center gap-2 rounded-full border-2 border-neutral-900 px-6 py-2.5 text-sm font-bold uppercase tracking-wide text-neutral-900 hover:bg-neutral-900 hover:text-white">Store Details</Link>
              </div>
            </div>
          </div>

          <div className="overflow-hidden rounded-2xl border border-neutral-200">
            <iframe
              title="Map of MattressTown Fort Lauderdale"
              src="https://www.google.com/maps?q=2677+N+Federal+Hwy,+Fort+Lauderdale,+FL+33306&output=embed"
              className="h-full min-h-[300px] w-full sm:min-h-[420px]"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </div>

        {/* Areas served - internal links for SEO */}
        <section className="mt-10 sm:mt-14">
          <h2 className="text-2xl font-bold uppercase text-neutral-900">Areas We Serve</h2>
          <p className="mt-2 text-neutral-600">Delivery throughout Miami-Dade, Broward &amp; Palm Beach counties, including:</p>
          <div className="mt-5 grid grid-cols-2 gap-4 sm:grid-cols-4">
            {CITY_PAGES.map((c) => (
              <Link key={c.slug} to={`/mattress-store/${c.slug}`} className="rounded-xl border border-neutral-200 bg-white p-5 text-center hover-lift">
                <MapPin className="mx-auto h-6 w-6 text-[#D0021B]" aria-hidden="true" />
                <span className="mt-2 block font-bold text-neutral-900">{c.city}</span>
                <span className="text-xs text-neutral-500">{c.county}</span>
              </Link>
            ))}
          </div>
        </section>
      </div>
    </>
  );
}
